import { Card, CardContent, CardHeader, Divider, TextField } from "@mui/material";
import { List, ListItem, ListItemText, Switch, Box } from "@mui/material";
import { useEffect, useState } from "react";
import {
    CompanySetting,
    restoreCompanySettings,
    updateCompanySettings
} from "../companySettingsAPI";
import { PasswordConfirmationDialog } from "../dialogs/PasswordConfirmationDialog";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { useAppDispatch } from "../../../../app/hooks";
import { CardActionButtons } from "./CardActionButtons";
import { Localized } from "@fluent/react";
import { RestoreSettingsPasswordConfirmationDialog } from "../dialogs/RestoreSettingsPasswordConfirmationDialog";

interface CompanyTaskReportSettingCardProps {
    companySettings: CompanySetting[];
    onUpdate: (updatedSettings: CompanySetting[]) => void;
}

export const CompanyTaskReportSettingCard: React.FC<CompanyTaskReportSettingCardProps> = ({
    companySettings,
    onUpdate
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const dispatch = useAppDispatch();

    // --- original ---
    const taskReportEnabledOrg = companySettings.find(s => s.code === "TASK_REPORT_ENABLED");
    const taskReportTimeOrg = companySettings.find(s => s.code === "TASK_REPORT_TIME");
    const taskReportAllTasksOrg = companySettings.find(s => s.code === "TASK_REPORT_ALL_TASKS");
    const taskReportIncludeOverdueOrg = companySettings.find(s => s.code === "TASK_REPORT_INCLUDE_OVERDUE");
    const taskReportDueInDaysOrg = companySettings.find(s => s.code === "TASK_REPORT_DUE_IN_DAYS");

    // ---state ---
    const [taskReportEnabled, setTaskReportEnabled] = useState<CompanySetting | undefined>(taskReportEnabledOrg);
    const [taskReportTime, setTaskReportTime] = useState<CompanySetting | undefined>(taskReportTimeOrg);
    const [taskReportAllTasks, setTaskReportAllTasks] = useState<CompanySetting | undefined>(taskReportAllTasksOrg);
    const [taskReportIncludeOverdue, setTaskReportIncludeOverdue] =
        useState<CompanySetting | undefined>(taskReportIncludeOverdueOrg);
    const [taskReportDueInDays, setTaskReportDueInDays] =
        useState<CompanySetting | undefined>(taskReportDueInDaysOrg);

    const isDefault =
        taskReportEnabled?.isDefault === true &&
        taskReportTime?.isDefault === true &&
        taskReportAllTasks?.isDefault === true &&
        taskReportIncludeOverdue?.isDefault === true &&
        taskReportDueInDays?.isDefault === true;

    // --- Handlers for toggles ---
    const toggleTaskReportEnabled = () => {
        setTaskReportEnabled(prev => (prev ? { ...prev, isActive: !prev.isActive } : undefined));
    };

    const toggleTaskReportAllTasks = () => {
        setTaskReportAllTasks(prev => (prev ? { ...prev, isActive: !prev.isActive } : undefined));
    };

    const toggleTaskReportIncludeOverdue = () => {
        setTaskReportIncludeOverdue(prev => (prev ? { ...prev, isActive: !prev.isActive } : undefined));
    };

    const toggleTaskReportDueInDays = () => {
        setTaskReportDueInDays(prev =>
            prev
                ? { ...prev, isActive: !prev.isActive }
                : undefined
        );
    };

    // --- Report time ---
    const handleTaskReportTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setTaskReportTime(prev =>
            prev ? { ...prev, value: newValue } : { code: "TASK_REPORT_TIME", value: newValue } as CompanySetting
        );
    };

    const handleTaskReportTimeBlur = () => {
        const defaultTime = "08:00";
        const safePrev = taskReportTime ?? { value: defaultTime };
        const value = safePrev.value && safePrev.value.length > 0 ? safePrev.value : defaultTime;
        setTaskReportTime(prev =>
            prev ? { ...prev, value } : { code: "TASK_REPORT_TIME", value } as CompanySetting
        );
    };

    const handleTaskReportDueInDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let newValue = parseInt(e.target.value, 10);
        if (isNaN(newValue) || newValue < 0) {
            newValue = 0;
        }
        setTaskReportDueInDays(prev =>
            prev
                ? { ...prev, value: newValue.toString() }
                : ({ code: "TASK_REPORT_DUE_IN_DAYS", value: newValue.toString() } as CompanySetting)
        );
    };

    const handleTaskReportDueInDaysBlur = () => {
        const defaultDays = 3;
        const safePrev = taskReportDueInDays ?? { value: defaultDays.toString() };
        const numericValue = parseInt(safePrev.value, 10);
        const val = !isNaN(numericValue) && numericValue >= 0 ? numericValue : defaultDays;
        setTaskReportDueInDays(prev =>
            prev
                ? { ...prev, value: val.toString() }
                : ({ code: "TASK_REPORT_DUE_IN_DAYS", value: val.toString() } as CompanySetting)
        );
    };

    useEffect(() => {
        setTaskReportEnabled(taskReportEnabledOrg);
        setTaskReportTime(taskReportTimeOrg);
        setTaskReportAllTasks(taskReportAllTasksOrg);
        setTaskReportIncludeOverdue(taskReportIncludeOverdueOrg);
        setTaskReportDueInDays(taskReportDueInDaysOrg);
    }, [companySettings, isEditing]);

    const handleRestore = () => {
        setRestoreDialogOpen(true);
    };

    const handleSave = () => {
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
        setServerError("");
    };

    const handleCloseRestore = () => {
        setRestoreDialogOpen(false);
        setServerError("");
    };

    const handleConfirmRestore = async (password: string) => {
        if (!password) return;
        setSaving(true);
        setServerError("");

        try {
            const result = await dispatch(
                restoreCompanySettings({
                    password: password,
                    settings: [
                        "TASK_REPORT_ENABLED",
                        "TASK_REPORT_TIME",
                        "TASK_REPORT_ALL_TASKS",
                        "TASK_REPORT_INCLUDE_OVERDUE",
                        "TASK_REPORT_DUE_IN_DAYS"
                    ]
                })
            );

            if (restoreCompanySettings.fulfilled.match(result)) {
                const settings = result.payload as CompanySetting[];
                onUpdate(settings);

                setTaskReportEnabled(settings.find(f => f.code === "TASK_REPORT_ENABLED") ?? taskReportEnabled);
                setTaskReportTime(settings.find(f => f.code === "TASK_REPORT_TIME") ?? taskReportTime);
                setTaskReportAllTasks(settings.find(f => f.code === "TASK_REPORT_ALL_TASKS") ?? taskReportAllTasks);
                setTaskReportIncludeOverdue(
                    settings.find(f => f.code === "TASK_REPORT_INCLUDE_OVERDUE") ?? taskReportIncludeOverdue
                );
                setTaskReportDueInDays(
                    settings.find(f => f.code === "TASK_REPORT_DUE_IN_DAYS") ?? taskReportDueInDays
                );

                setRestoreDialogOpen(false);
                setIsEditing(false);
                dispatch(showSuccess("saved"));
            } else {
                let errorMsg = "error";

                if (result.payload && result.payload.kind === "http") {
                    const problem = result.payload.problem;
                    if (problem) {
                        errorMsg = problem.title;
                    }
                }
                setServerError(errorMsg);
            }
        } catch {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmSave = async (password: string) => {
        if (
            !password ||
            !taskReportEnabled ||
            !taskReportTime ||
            !taskReportAllTasks ||
            !taskReportIncludeOverdue ||
            !taskReportDueInDays
        ) {
            return;
        }

        setSaving(true);
        setServerError("");

        try {
            const settingsToUpdate: CompanySetting[] = [
                taskReportEnabled,
                taskReportTime,
                taskReportAllTasks,
                taskReportIncludeOverdue,
                taskReportDueInDays
            ];

            const result = await dispatch(
                updateCompanySettings({
                    password: password,
                    settings: settingsToUpdate
                })
            );

            if (updateCompanySettings.fulfilled.match(result)) {
                const settings = result.payload as CompanySetting[];
                onUpdate(settings);

                setTaskReportEnabled(settings.find(f => f.code === "TASK_REPORT_ENABLED") ?? taskReportEnabled);
                setTaskReportTime(settings.find(f => f.code === "TASK_REPORT_TIME") ?? taskReportTime);
                setTaskReportAllTasks(settings.find(f => f.code === "TASK_REPORT_ALL_TASKS") ?? taskReportAllTasks);
                setTaskReportIncludeOverdue(
                    settings.find(f => f.code === "TASK_REPORT_INCLUDE_OVERDUE") ?? taskReportIncludeOverdue
                );
                setTaskReportDueInDays(
                    settings.find(f => f.code === "TASK_REPORT_DUE_IN_DAYS") ?? taskReportDueInDays
                );

                setDialogOpen(false);
                setIsEditing(false);
                dispatch(showSuccess("saved"));
            } else {
                let errorMsg = "error";
                if (result.payload && result.payload.kind === "http") {
                    const problem = result.payload.problem;
                    if (problem) {
                        errorMsg = problem.title;
                    }
                }
                setServerError(errorMsg);
            }
        } catch {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card sx={{ width: "100%", maxWidth: 500, borderRadius: 2, boxShadow: 3 }}>
            <CardHeader
                title={
                    <Localized id="company-settings-task-report">
                        <span>_Raport zadań</span>
                    </Localized>
                }
                sx={{
                    textAlign: "center",
                    bgcolor: "primary.main",
                    color: "white",
                    py: 1,
                    "& .MuiCardHeader-title": { fontSize: "1rem" }
                }}
            />
            <CardContent>
                <List>
                    <ListItem>
                        <ListItemText
                            primary={
                                <Localized id="company-settings-task-report-desc">
                                    <span>_Codzienny raport otwartych zadań wysyłany e-mailem.</span>
                                </Localized>
                            }
                        />
                    </ListItem>

                    <Box sx={{ pl: 2 }}>
                        <ListItem>
                            <ListItemText
                                primary={
                                    <Localized id="company-settings-task-report-enabled">
                                        <span>_Włącz dzienny raport zadań</span>
                                    </Localized>
                                }
                            />
                            <Switch
                                checked={taskReportEnabled?.isActive}
                                onChange={toggleTaskReportEnabled}
                                disabled={!isEditing || taskReportEnabled?.isFixed}
                            />
                        </ListItem>

                        <ListItem>
                            <TextField
                                label={
                                    <Localized id="company-settings-task-report-time">
                                        <span>_Godzina wysyłki</span>
                                    </Localized>
                                }
                                type="time"
                                value={taskReportTime?.value ?? "08:00"}
                                onChange={handleTaskReportTimeChange}
                                onBlur={handleTaskReportTimeBlur}
                                disabled={!isEditing || taskReportTime?.isFixed}
                                variant="outlined"
                                fullWidth
                                size="small"
                                inputProps={{
                                    step: 300
                                }}
                            />
                        </ListItem>

                        <>
                            <ListItem>
                                <ListItemText
                                    primary={
                                        <Localized id="company-settings-task-report-all-tasks">
                                            <span>_Pokaż wszystkie otwarte zadania</span>
                                        </Localized>
                                    }
                                />
                                <Switch
                                    checked={taskReportAllTasks?.isActive}
                                    onChange={toggleTaskReportAllTasks}
                                    disabled={!isEditing || taskReportAllTasks?.isFixed}
                                />
                            </ListItem>

                            <>
                                <ListItem>
                                    <ListItemText
                                        primary={
                                            <Localized id="company-settings-task-report-include-overdue">
                                                <span>_Uwzględnij zadania po terminie</span>
                                            </Localized>
                                        }
                                    />
                                    <Switch
                                        checked={taskReportIncludeOverdue?.isActive}
                                        onChange={toggleTaskReportIncludeOverdue}
                                        disabled={!isEditing || taskReportIncludeOverdue?.isFixed}
                                    />
                                </ListItem>

                                <ListItem>
                                    <ListItemText
                                        primary={
                                            <Localized id="company-settings-task-report-due-in-days">
                                                <span>_Zadania z terminem w ciągu (dni)</span>
                                            </Localized>
                                        }
                                    />
                                    <Switch
                                        checked={taskReportDueInDays?.isActive}
                                        onChange={toggleTaskReportDueInDays}
                                        disabled={!isEditing || taskReportDueInDays?.isFixed}
                                    />
                                </ListItem>

                                <ListItem>
                                    <TextField
                                        label={
                                            <Localized id="days">
                                                <span>_days</span>
                                            </Localized>
                                        }
                                        type="number"
                                        value={taskReportDueInDays?.value ?? "3"}
                                        onChange={handleTaskReportDueInDaysChange}
                                        onBlur={handleTaskReportDueInDaysBlur}
                                        disabled={
                                            !isEditing ||
                                            !taskReportDueInDays?.isActive ||
                                            taskReportDueInDays?.isFixed
                                        }
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        inputProps={{
                                            min: 0,
                                            step: 1
                                        }}
                                    />
                                </ListItem>

                            </>
                        </>
                    </Box>
                </List>

                <Divider sx={{ width: "100%", mt: 3 }} />

                <CardActionButtons
                    isDefault={isDefault}
                    isEditing={isEditing}
                    onRestore={handleRestore}
                    onSave={handleSave}
                    onCancel={() => setIsEditing(false)}
                    onEdit={() => setIsEditing(true)}
                />
            </CardContent>

            {dialogOpen && (
                <PasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={handleClose}
                    onConfirm={handleConfirmSave}
                />
            )}
            {restoreDialogOpen && (
                <RestoreSettingsPasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={handleCloseRestore}
                    onConfirm={handleConfirmRestore}
                />
            )}
        </Card>
    );
};
